import { config } from '../config/config';
import { supabase } from '../lib/supabase';

// Constants pour les appels aux fonctions Edge
const SUPABASE_FUNCTIONS_URL = 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/functions/v1';
const SUPABASE_ANON_KEY = config.supabaseKey || '';


// Interface pour les détails de paiement
export interface CampayElearningPaymentDetails {
  amount: number;
  phoneNumber: string;
  operator: string;
  order_id: string;
  order_number: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
}

// Interface pour la réponse d'initiation de paiement
export interface CampayElearningPaymentResponse {
  transaction: {
    id: string;
    amount: number;
    status: string;
    payment_method: string;
    payment_reference: string | null;
    payment_url: string | null;
  };
  payment_reference: string;
  payment_url: string;
}

// Interface pour la réponse de statut de paiement
export interface CampayElearningStatusResponse {
  status: 'success' | 'pending' | 'failed' | 'SUCCESSFUL' | 'PENDING' | 'FAILED';
  data: {
    order_id: string;
    payment_reference: string;
    status: string;
    [key: string]: any;
  };
}

/**
 * Initie un paiement via la fonction Edge Campay spécifique pour l'e-learning
 */
export const initiateCampayElearningEdgePayment = async (
  details: CampayElearningPaymentDetails
): Promise<CampayElearningPaymentResponse> => {
  try {
    // Utiliser la fonction edge spécifique pour l'e-learning
    const response = await fetch(`${config.supabaseUrl}/functions/v1/elearning-campay-initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify(details),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Échec de l'initiation du paiement");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Erreur lors de l'initiation du paiement Campay pour e-learning:", error);
    throw error;
  }
};

/**
 * Vérifie le statut d'un paiement CamPay via la fonction Edge
 */
export const checkCampayElearningEdgePaymentStatus = async (
  reference: string,
  order_id: string
): Promise<CampayElearningStatusResponse> => {
  const requestId = Math.random().toString(36).substring(2, 10);
  const fetchStartTime = Date.now();
  
  try {
    console.log(`[CampayElearningEdge][${requestId}] Vérification du statut pour la référence: ${reference}`);
    
    // Récupérer la commande depuis la base de données
    const startTime = Date.now();
    const { data: order, error } = await supabase
      .from('elearn_orders')
      .select('status, payment_reference, created_at')
      .eq('id', order_id)
      .single();
    
    const queryTime = Date.now() - startTime;
    console.log(`[CampayElearningEdge][${requestId}] Requête Supabase terminée en ${queryTime}ms`);
    
    if (error || !order) {
      console.error(`[CampayElearningEdge][${requestId}] Erreur ou commande introuvable:`, error);
      throw new Error(`Commande introuvable: ${error?.message || 'Erreur inconnue'}`);
    }
    
    console.log(`[CampayElearningEdge][${requestId}] Commande trouvée avec statut: ${order.status}`);
    
    // Si la commande est déjà complétée, retourner directement le statut
    if (order.status === 'completed') {
      console.log(`[CampayElearningEdge][${requestId}] La commande ${order_id} est déjà complétée`);
      
      return {
        status: 'success',
        data: { 
          order_id,
          payment_reference: reference,
          status: 'completed',
          created_at: order.created_at
        } 
      };
    }
    
    console.log(`[CampayElearningEdge][${requestId}] La commande ${order_id} n'est pas encore payée, vérification via la fonction edge`);
    
    // Appeler la fonction Edge pour vérifier le statut du paiement
    const response = await fetch(
      `${SUPABASE_FUNCTIONS_URL}/elearning-campay-check`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          reference,
          order_id
        })
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur lors de la vérification du statut: ${response.status} ${errorText}`);
    }
    
    const responseData: CampayElearningStatusResponse = await response.json();
    
    const fetchTime = Date.now() - fetchStartTime;
    console.log(`[CampayElearningEdge][${requestId}] Vérification terminée en ${fetchTime}ms - Statut: ${responseData.status}`);
    
    return responseData;
  } catch (error) {
    console.error(`[CampayElearningEdge][${requestId}] Erreur lors de la vérification du statut du paiement:`, error);
    throw error;
  }
};
